import { useEffect } from 'react';
import { TaskState } from './TaskStatusBadge';

interface UseTaskPollingOptions {
    /**
     * Array of tasks or task states to monitor
     */
    tasks: Array<{ taskState: TaskState | string }> | null | undefined;

    /**
     * Callback to execute when polling (e.g., refetch query)
     */
    onPoll: () => void;

    /**
     * Polling interval in milliseconds
     * @default 3000
     */
    intervalMs?: number;

    /**
     * Custom condition to determine if polling should be active
     * If not provided, will poll when any task is in WAITING or PROCESSING state
     */
    shouldPoll?: (tasks: Array<{ taskState: TaskState | string }>) => boolean;

    /**
     * Enable or disable polling
     * @default true
     */
    enabled?: boolean;
}

/**
 * useTaskPolling - Custom hook for automatic task polling
 *
 * Automatically polls tasks that are in progress (WAITING or PROCESSING state).
 * Stops polling when all tasks are in terminal states.
 *
 * @example
 * ```tsx
 * // Basic usage
 * useTaskPolling({
 *   tasks: data.templateTasks,
 *   onPoll: () => refetch(),
 *   intervalMs: 3000
 * });
 *
 * // Custom polling condition
 * useTaskPolling({
 *   tasks: data.tasks,
 *   onPoll: handleRefresh,
 *   shouldPoll: (tasks) => tasks.some(t => t.taskState === 'PROCESSING' && t.priority === 'HIGH'),
 *   intervalMs: 1000
 * });
 *
 * // Conditional polling
 * useTaskPolling({
 *   tasks: data.tasks,
 *   onPoll: refetch,
 *   enabled: isPollingEnabled
 * });
 * ```
 */
export function useTaskPolling(options: UseTaskPollingOptions) {
    const { tasks, onPoll, intervalMs = 3000, shouldPoll, enabled = true } = options;

    useEffect(() => {
        if (!enabled || !tasks || tasks.length === 0) {
            return;
        }

        // Determine if we should poll
        const shouldStartPolling = shouldPoll ? shouldPoll(tasks) : tasks.some((task) => task.taskState === 'WAITING' || task.taskState === 'PROCESSING');

        if (!shouldStartPolling) {
            return;
        }

        // Start polling
        const intervalId = setInterval(() => {
            onPoll();
        }, intervalMs);

        // Cleanup on unmount or when dependencies change
        return () => clearInterval(intervalId);
    }, [tasks, onPoll, intervalMs, shouldPoll, enabled]);
}

export default useTaskPolling;
